// Admin API functions
import { authenticatedFetch } from './auth';

const API_URL = '/api';

export interface StudyAssociation {
  id: string;
  name: string;
  org_number: string;
}

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  study_association_id: string;
  study_association_name: string;
  member_id: string | null;
  member_organization_name: string | null;
  email_verified: boolean;
  confirmed_at: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  created_at: string;
}

// =====================================================
// STUDY ASSOCIATIONS
// =====================================================

export async function getStudyAssociations(): Promise<StudyAssociation[]> {
  const response = await authenticatedFetch(`${API_URL}/study-associations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get study associations');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to get study associations');
  }

  return data.study_associations;
}

// =====================================================
// USER MANAGEMENT
// =====================================================

export async function getUsers(adminId: string, includePending: boolean = true): Promise<User[]> {
  const response = await authenticatedFetch(`${API_URL}/admin/get-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      include_pending: includePending,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get users');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to get users');
  }

  return data.users;
}

export async function approveUser(adminId: string, userId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/approve-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to approve user');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to approve user');
  }

  return data;
}

export async function rejectUser(adminId: string, userId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/reject-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject user');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to reject user');
  }

  return data;
}

export async function updateUser(
  adminId: string,
  userId: string,
  updates: {
    email?: string;
    full_name?: string;
    study_association_id?: string;
    role?: string;
    member_id?: string | null;
    is_member_admin?: boolean;
  }
) {
  const response = await authenticatedFetch(`${API_URL}/admin/update-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_id: userId,
      email: updates.email,
      full_name: updates.full_name,
      study_association_id: updates.study_association_id,
      role: updates.role,
      member_id: updates.member_id,
      is_member_admin: updates.is_member_admin,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update user');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to update user');
  }

  return data;
}

// =====================================================
// STUDY ASSOCIATION MANAGEMENT
// =====================================================

export interface StudyAssociationDetailed {
  id: string;
  name: string;
  organization_number: string;
  contact_email: string;
  contact_phone: string | null;
  address: string | null;
  logo_path: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  user_count: number;
}

export async function getAllStudyAssociations(): Promise<StudyAssociationDetailed[]> {
  const response = await authenticatedFetch(`${API_URL}/admin/get-all-study-associations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get study associations');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to get study associations');
  }

  return data.associations;
}

export async function createStudyAssociation(
  adminId: string,
  association: {
    name: string;
    organization_number: string;
    contact_email: string;
    contact_phone?: string;
    address?: string;
  }
) {
  const response = await authenticatedFetch(`${API_URL}/admin/create-study-association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      name: association.name,
      organization_number: association.organization_number,
      contact_email: association.contact_email,
      contact_phone: association.contact_phone,
      address: association.address,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create study association');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to create study association');
  }

  return data;
}

export async function updateStudyAssociation(
  adminId: string,
  associationId: string,
  updates: {
    name?: string;
    organization_number?: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    active?: boolean;
  }
) {
  const response = await authenticatedFetch(`${API_URL}/admin/update-study-association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      association_id: associationId,
      ...updates,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update study association');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to update study association');
  }

  return data;
}

export async function deactivateStudyAssociation(adminId: string, associationId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/deactivate-study-association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      association_id: associationId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to deactivate study association');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to deactivate study association');
  }

  return data;
}

export async function reactivateStudyAssociation(adminId: string, associationId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/reactivate-study-association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      association_id: associationId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reactivate study association');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to reactivate study association');
  }

  return data;
}

// Get users for a specific study association
export interface AssociationUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  created_at: string;
}

export async function getAssociationUsers(adminId: string, associationId: string): Promise<AssociationUser[]> {
  const response = await authenticatedFetch(`${API_URL}/admin/get-association-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      association_id: associationId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get association users');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to get association users');
  }

  return data.users;
}

// Move users to another study association
export async function moveUsersToAssociation(adminId: string, userIds: string[], targetAssociationId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/move-users-to-association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_ids: userIds,
      target_association_id: targetAssociationId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to move users');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to move users');
  }

  return data;
}

// Delete users
export async function deleteUsers(adminId: string, userIds: string[]) {
  const response = await authenticatedFetch(`${API_URL}/admin/delete-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_ids: userIds,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete users');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to delete users');
  }

  return data;
}

// Delete study association (hard delete)
export async function deleteStudyAssociation(adminId: string, associationId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/delete-study-association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      association_id: associationId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete study association');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to delete study association');
  }

  return data;
}

// Delete user with email notification (admin only)
export async function deleteUserWithNotification(adminId: string, userId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/delete-user-with-notification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete user');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to delete user');
  }

  return data;
}

// Notify user about profile update
export async function notifyUserUpdate(userEmail: string, userFullName: string, updatedFields: Record<string, string>) {
  const response = await authenticatedFetch(`${API_URL}/admin/notify-user-update`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      user_email: userEmail,
      user_full_name: userFullName,
      updated_fields: updatedFields,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to send notification');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to send notification');
  }

  return data;
}

// =====================================================
// SOFT DELETE USER MANAGEMENT
// =====================================================

export interface DeletedUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  study_association_id: string;
  study_association_name: string;
  deleted_at: string;
  created_at: string;
}

// Soft delete user (admin only, sets deleted_at)
export async function softDeleteUser(adminId: string, userId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/soft-delete-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete user');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to delete user');
  }

  return data;
}

// Get deleted users (systemadmin only)
export async function getDeletedUsers(adminId: string): Promise<DeletedUser[]> {
  const response = await authenticatedFetch(`${API_URL}/admin/get-deleted-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get deleted users');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to get deleted users');
  }

  return data.users;
}

// Reactivate user (systemadmin only)
export async function reactivateUser(adminId: string, userId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/reactivate-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reactivate user');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to reactivate user');
  }

  return data;
}

// Permanently delete user (systemadmin only, hard delete from database)
export async function permanentDeleteUser(adminId: string, userId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/permanent-delete-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to permanently delete user');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to permanently delete user');
  }

  return data;
}

// Manually verify email (systemadmin only)
export async function verifyUserEmail(adminId: string, userId: string) {
  const response = await authenticatedFetch(`${API_URL}/admin/verify-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      user_id: userId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify email');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to verify email');
  }

  return data;
}

// =====================================================
// SYSTEM SETTINGS MANAGEMENT (systemadmin only)
// =====================================================

export interface SystemSettings {
  smtp: {
    id: string;
    host: string;
    port: number;
    username: string;
    from_email: string;
    from_name: string;
    openai_api_key?: string;
    serper_api_key?: string;
  } | null;
  validation_rules: {
    id: string;
    min_knowledge_items: number;
    min_skills_items: number;
    min_competence_items: number;
    min_ects_hours: number;
    max_ects_hours: number;
    min_validation_score: number;
  } | null;
  ai_prompts: {
    validation: {
      id: string;
      content: string;
    } | null;
    generation: {
      id: string;
      content: string;
    } | null;
    exam: {
      id: string;
      content: string;
    } | null;
    literature_suggest: {
      id: string;
      content: string;
    } | null;
    improvement: {
      id: string;
      content: string;
    } | null;
  };
  legal_text: {
    id: string;
    content: string;
  } | null;
}

// Get all system settings
export async function getSystemSettings(adminId: string): Promise<SystemSettings> {
  const response = await authenticatedFetch(`${API_URL}/admin/get-settings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get settings');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to get settings');
  }

  return data.settings;
}

// Update SMTP settings
export async function updateSMTPSettings(
  adminId: string,
  settings: {
    host?: string;
    port?: number;
    username?: string;
    password?: string;
    from_email?: string;
    from_name?: string;
    openai_api_key?: string;
    serper_api_key?: string;
  }
) {
  const response = await authenticatedFetch(`${API_URL}/admin/update-smtp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      ...settings,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update SMTP settings');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to update SMTP settings');
  }

  return data;
}

// Update validation rules
export async function updateValidationRules(
  adminId: string,
  rules: {
    min_knowledge_items?: number;
    min_skills_items?: number;
    min_competence_items?: number;
    min_ects_hours?: number;
    max_ects_hours?: number;
    min_validation_score?: number;
    // New threshold fields
    auto_approve_threshold?: number;
    manual_review_threshold?: number;
    minimum_hours?: number;
  }
) {
  const response = await authenticatedFetch(`${API_URL}/admin/update-validation-rules`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      ...rules,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update validation rules');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to update validation rules');
  }

  return data;
}

// Update AI prompts
export async function updateAIPrompts(
  adminId: string,
  prompts: {
    validation_prompt_id?: string;
    validation_prompt?: string;
    generation_prompt_id?: string;
    generation_prompt?: string;
    exam_prompt_id?: string;
    exam_prompt?: string;
    literature_suggest_prompt_id?: string;
    literature_suggest_prompt?: string;
    improvement_prompt_id?: string;
    improvement_prompt?: string;
  }
) {
  const response = await authenticatedFetch(`${API_URL}/admin/update-ai-prompts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      ...prompts,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update AI prompts');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to update AI prompts');
  }

  return data;
}

// Update legal framework text
export async function updateLegalText(
  adminId: string,
  legalTextId: string | null,
  legalText: string
) {
  const response = await authenticatedFetch(`${API_URL}/admin/update-legal-text`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      admin_id: adminId,
      legal_text_id: legalTextId,
      legal_text: legalText,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update legal text');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to update legal text');
  }

  return data;
}

// =====================================================
// MEMBERS (ORGANIZATIONS)
// =====================================================

export interface Member {
  id: string;
  organization_name: string;
  organization_number?: string;
  contact_email?: string;
  technical_email?: string;
  contact_phone?: string;
  address?: string;
  logo_path?: string | null;
  status: string;
  ai_requests_count: number;
  study_association_id: string;
  study_association_name?: string;
  study_association_logo_path?: string | null;
  user_count?: number;
  sensor_count?: number;
  created_at: string;
  updated_at: string;
}

export async function getMembers(studyAssociationId?: string): Promise<Member[]> {
  const url = studyAssociationId
    ? `${API_URL}/admin/members/by-association/${studyAssociationId}`
    : `${API_URL}/admin/members`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch members');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch members');
  }

  return data.members;
}

export async function getMember(memberId: string): Promise<Member> {
  const response = await authenticatedFetch(`${API_URL}/admin/members/${memberId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch member');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch member');
  }

  return data.member;
}

export async function createMember(memberData: {
  study_association_id: string;
  organization_name: string;
  organization_number?: string;
  contact_email?: string;
  technical_email?: string;
  contact_phone?: string;
  address?: string;
  approved_domain?: string;  // Valgfritt: auto-godkjenn hoveddomenet samtidig som org opprettes
}): Promise<Member> {
  const response = await authenticatedFetch(`${API_URL}/admin/members`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(memberData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create member');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to create member');
  }

  return data.member;
}

export async function updateMember(
  memberId: string,
  updates: Partial<Member>
): Promise<Member> {
  const response = await authenticatedFetch(`${API_URL}/admin/members/${memberId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update member');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to update member');
  }

  return data.member;
}

/**
 * Oppdaterer kun kontaktinfo (adresse/telefon/e-post). Tilgjengelig for medlem-admin
 * på egen organisasjon, samt studieforbund-admin og systemadmin.
 */
export async function updateMemberContact(
  memberId: string,
  contact: { address?: string | null; contact_phone?: string | null; contact_email?: string | null; technical_email?: string | null }
): Promise<Member> {
  const response = await authenticatedFetch(`${API_URL}/admin/members/${memberId}/contact`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(contact),
  });

  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Kunne ikke oppdatere kontaktinfo');
  }
  return data.member;
}

export async function deleteMember(memberId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_URL}/admin/members/${memberId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete member');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to delete member');
  }
}

// =====================================================
// SENSORS
// =====================================================

export interface Sensor {
  id: string;
  email: string;
  full_name: string;
  role: string;
  status: string;
  member_id: string | null;
  member_name?: string;
  study_association_id: string | null;
  study_association_name?: string;
  is_sensor?: boolean;
  is_member_admin?: boolean;
  /** True hvis brukeren har sensor-flagg men primærrolle er noe annet
   *  (typisk medlem/medlem-admin med ekstra sensor-tilgang). Slike
   *  brukere skal ikke få e-post/status endret via sensor-fanen. */
  is_hybrid?: boolean;
  assigned_courses_count?: number;
  organizations?: Array<{
    org_id: string;
    org_name: string;
    assoc_id: string;
    assoc_name: string;
  }>;
  created_at: string;
}

export interface SensorAssignment {
  id: string;
  course_instance_id: string;
  course_name?: string;
  sensor_user_id: string;
  sensor_name?: string;
  sensor_email?: string;
  role: string;
  assigned_at: string;
}

export interface CourseInstance {
  id: string;
  exam_set_id: string;
  exam_title?: string;
  member_id: string;
  member_name?: string;
  study_association_id: string;
  study_association_name?: string;
  name: string;
  course_code?: string;
  start_date?: string;
  end_date?: string;
  // v6.6.2: api_enabled/api_link fjernet
  max_candidates?: number;
  status: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  candidate_count?: number;
  sensor_count?: number;
}

export async function getSensors(params?: {
  member_id?: string;
  study_association_id?: string;
}): Promise<Sensor[]> {
  const queryParams = new URLSearchParams();
  if (params?.member_id) queryParams.append('member_id', params.member_id);
  if (params?.study_association_id) queryParams.append('study_association_id', params.study_association_id);

  const url = `${API_URL}/admin/sensors${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch sensors');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch sensors');
  }

  return data.sensors;
}

export async function getCourseInstances(params?: {
  exam_set_id?: string;
  member_id?: string;
  study_association_id?: string;
}): Promise<CourseInstance[]> {
  const queryParams = new URLSearchParams();
  if (params?.exam_set_id) queryParams.append('exam_set_id', params.exam_set_id);
  if (params?.member_id) queryParams.append('member_id', params.member_id);
  if (params?.study_association_id) queryParams.append('study_association_id', params.study_association_id);

  const url = `${API_URL}/admin/course-instances${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch course instances');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch course instances');
  }

  return data.instances;
}

export async function createSensor(sensorData: {
  email: string;
  full_name: string;
  member_id?: string;
  study_association_id?: string;
}): Promise<Sensor & { temp_password: string }> {
  const response = await authenticatedFetch(`${API_URL}/admin/create-sensor`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sensorData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create sensor');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to create sensor');
  }

  return { ...data.sensor, temp_password: data.temp_password };
}

export async function deleteSensor(sensorId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_URL}/admin/sensors/${sensorId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete sensor');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to delete sensor');
  }
}

export async function updateSensor(sensorId: string, updateData: {
  full_name?: string;
  email?: string;
  status?: string;
  member_id?: string | null;
  study_association_id?: string | null;
}): Promise<Sensor> {
  const response = await authenticatedFetch(`${API_URL}/admin/sensors/${sensorId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update sensor');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to update sensor');
  }

  return data.sensor;
}

/**
 * Kastet av assignSensorToCourse ved 409 habilitet_conflict fra backend (v6.2.4).
 *   - blocked=true → tildeling er UMULIG i denne konteksten (selv-tildeling, eller
 *     rolle uten bypass-myndighet). Frontend skal vise blokkerings-melding.
 *   - blocked=false + canBypass=true → dialog + evt. begrunnelse (requiresReason).
 */
export class HabilitetConflictError extends Error {
  code: 'habilitet_conflict';
  warningCode?: string;
  canBypass: boolean;
  blocked: boolean;
  blockedReason?: string;
  requiresReason: boolean;
  reasonMinLength: number;
  reasonPrompt?: string;
  fagligConfirmationNotice?: string;
  constructor(opts: {
    message: string;
    warningCode?: string;
    canBypass?: boolean;
    blocked?: boolean;
    blockedReason?: string;
    requiresReason?: boolean;
    reasonMinLength?: number;
    reasonPrompt?: string;
    fagligConfirmationNotice?: string;
  }) {
    super(opts.message);
    this.name = 'HabilitetConflictError';
    this.code = 'habilitet_conflict';
    this.warningCode = opts.warningCode;
    this.canBypass = opts.canBypass ?? true;
    this.blocked = opts.blocked ?? false;
    this.blockedReason = opts.blockedReason;
    this.requiresReason = opts.requiresReason ?? false;
    this.reasonMinLength = opts.reasonMinLength ?? 0;
    this.reasonPrompt = opts.reasonPrompt;
    this.fagligConfirmationNotice = opts.fagligConfirmationNotice;
  }
}

export async function assignSensorToCourse(assignmentData: {
  course_instance_id: string;
  sensor_user_id: string;
  role?: string;
  assigned_by?: string;
  confirmHabilitet?: boolean;
  habilitetReason?: string;
}): Promise<SensorAssignment> {
  const { confirmHabilitet, habilitetReason, ...rest } = assignmentData;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (confirmHabilitet) headers['X-Confirm-Habilitet'] = 'true';

  const body: Record<string, any> = { ...rest };
  if (confirmHabilitet && habilitetReason) body.habilitet_reason = habilitetReason;

  const response = await authenticatedFetch(`${API_URL}/admin/assign-sensor-to-course`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    if (response.status === 409 && error.error === 'habilitet_conflict') {
      throw new HabilitetConflictError({
        message: error.message || 'Habilitet-varsel',
        warningCode: error.warning_code,
        canBypass: error.can_bypass !== false,
        blocked: error.blocked === true,
        blockedReason: error.blocked_reason,
        requiresReason: error.requires_reason === true,
        reasonMinLength: error.reason_min_length || 0,
        reasonPrompt: error.reason_prompt,
        fagligConfirmationNotice: error.faglig_confirmation_notice
      });
    }
    throw new Error(error.error || error.message || 'Failed to assign sensor');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to assign sensor');
  }

  return data.assignment;
}

export async function unassignSensor(assignmentId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_URL}/admin/unassign-sensor/${assignmentId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unassign sensor');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to unassign sensor');
  }
}

// =====================================================
// LOGO UPLOAD
// =====================================================

export interface LogoUploadResult {
  success: boolean;
  logo_path?: string;
  has_transparency?: boolean;
  dimensions?: { width: number; height: number };
  message?: string;
  error?: string;
}

export interface LogoRequirements {
  min_dimension: number;
  max_file_size: number;
  max_file_size_mb: number;
  allowed_formats: string[];
  recommended: {
    format: string;
    dimensions: string;
    aspect_ratio: string;
  };
}

/**
 * Get logo upload requirements
 */
export async function getLogoRequirements(): Promise<LogoRequirements> {
  const response = await authenticatedFetch(`${API_URL}/admin/logo-requirements`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get logo requirements');
  }

  const data = await response.json();
  return data.requirements;
}

/**
 * Upload logo for a study association
 */
export async function uploadStudyAssociationLogo(
  associationId: string,
  file: File
): Promise<LogoUploadResult> {
  const formData = new FormData();
  formData.append('logo', file);

  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_URL}/admin/study-associations/${associationId}/logo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Kunne ikke laste opp logo',
    };
  }

  return data;
}

/**
 * Delete logo for a study association
 */
export async function deleteStudyAssociationLogo(associationId: string): Promise<void> {
  const response = await authenticatedFetch(
    `${API_URL}/admin/study-associations/${associationId}/logo`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke slette logo');
  }
}

/**
 * Upload logo for a member organization
 */
export async function uploadMemberLogo(
  memberId: string,
  file: File
): Promise<LogoUploadResult> {
  const formData = new FormData();
  formData.append('logo', file);

  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_URL}/admin/members/${memberId}/logo`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  const data = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: data.error || 'Kunne ikke laste opp logo',
    };
  }

  return data;
}

/**
 * Delete logo for a member organization
 */
export async function deleteMemberLogo(memberId: string): Promise<void> {
  const response = await authenticatedFetch(
    `${API_URL}/admin/members/${memberId}/logo`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke slette logo');
  }
}

// =====================================================
// USER INVITATIONS
// =====================================================

export interface UserInvitation {
  id: string;
  email: string;
  role: string;
  study_association_name: string;
  member_name: string | null;
  invited_by_name: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  status: 'pending' | 'accepted' | 'expired';
}

export interface InviteUserData {
  email: string;
  study_association_id?: string;
  member_id?: string | null;
  role?: string;
  is_member_admin?: boolean;
}

/**
 * Send invitation to a new user
 */
export async function inviteUser(data: InviteUserData): Promise<{ success: boolean; error?: string; invitation_id?: string }> {
  const response = await authenticatedFetch(`${API_URL}/admin/invite-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok || !result.success) {
    return {
      success: false,
      error: result.error || 'Kunne ikke sende invitasjon',
    };
  }

  return result;
}

/**
 * Get list of invitations
 */
export async function getInvitations(): Promise<UserInvitation[]> {
  const response = await authenticatedFetch(`${API_URL}/admin/invitations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke hente invitasjoner');
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Kunne ikke hente invitasjoner');
  }

  return data.invitations;
}

/**
 * Delete/cancel an invitation
 */
export async function deleteInvitation(invitationId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_URL}/admin/invitations/${invitationId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke slette invitasjon');
  }
}

/**
 * Dismiss en akseptert/utløpt invitasjon — fjerner den fra "Sendte invitasjoner"-listen
 * uten å slette DB-raden (som beholdes for revisjonsspor).
 */
export async function dismissInvitation(invitationId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_URL}/admin/invitations/${invitationId}/dismiss`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
  });
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Kunne ikke lukke varsel');
  }
}

/**
 * Resend an invitation
 */
export async function resendInvitation(invitationId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_URL}/admin/invitations/${invitationId}/resend`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke sende invitasjon på nytt');
  }
}
